import classNames from 'classnames';
import React from 'react';
import { AriaTextFieldOptions, useTextField } from 'react-aria';
import { useTranslation } from 'react-i18next';
import useSharedRef from '../../utils/use-shared-ref';

export type InputTextAreaProps = AriaTextFieldOptions<'textarea'> & {
  children?: React.ReactNode;
  className?: string;
  classNameLabel?: string;
  classNameLabelTitle?: string;
  animate?: boolean;
  optional?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  transparent?: boolean;
  autoHeight?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

const InputTextArea = React.forwardRef<HTMLTextAreaElement, InputTextAreaProps>(
  (props, ref) => {
    const {
      label,
      className,
      classNameLabel,
      classNameLabelTitle,
      animate,
      optional,
      rows = 3,
      minRows = 1,
      maxRows = 10,
      isDisabled,
      isReadOnly,
      transparent,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...otherProps
    } = props;

    const { t } = useTranslation('app');
    const sharedRef = useSharedRef<HTMLTextAreaElement>(ref);
    const lineHeightRef = React.useRef<number>(20);

    // Add validation for accessibility
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn(
        'InputTextArea: You must provide either a label, aria-label, or aria-labelledby prop for accessibility',
      );
    }

    const adjustHeight = React.useCallback(() => {
      const textarea = sharedRef.current;
      if (!textarea) return;

      // Store the current scroll position
      const scrollPos = textarea.scrollTop;

      // Reset height to allow proper scrollHeight calculation
      textarea.style.height = 'auto';

      // Calculate the new height
      const minHeight = lineHeightRef.current * minRows;
      const maxHeight = lineHeightRef.current * maxRows;
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, minHeight),
        maxHeight,
      );

      // Apply the new height
      textarea.style.height = `${newHeight}px`;

      // Restore scroll position
      textarea.scrollTop = scrollPos;

      // Show/hide scrollbar based on content
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [minRows, maxRows]);

    // Calculate initial line height
    React.useLayoutEffect(() => {
      const textarea = sharedRef.current;
      if (textarea) {
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseInt(computedStyle.lineHeight) || 20;
        lineHeightRef.current = lineHeight;

        // Initial height adjustment
        adjustHeight();
      }
    }, [adjustHeight]);

    // Handle value changes
    React.useEffect(() => {
      if (props.value !== undefined) {
        adjustHeight();
      }
    }, [props.value, adjustHeight]);

    const handleInput = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        props.onChange?.(e.target.value);
      },
      [adjustHeight, props.onChange],
    );

    const { labelProps, inputProps } = useTextField(
      {
        ...otherProps,
        label,
        inputElementType: 'textarea',
        isDisabled,
        isReadOnly,
        value: props.value === null ? '' : props.value,
      },
      sharedRef,
    );

    return (
      <label
        {...labelProps}
        className={classNames(
          'flex flex-col',
          'relative',
          isDisabled ? 'text-menu-text/50' : 'text-menu-text',
          classNameLabel,
        )}
      >
        {label && (
          <div className={classNames(classNameLabelTitle ?? 'mb-2 text-sm')}>
            {label}
            {optional && (
              <span className="opacity-50 ltr:ml-2 rtl:mr-2">
                {t('(Optional)')}
              </span>
            )}
          </div>
        )}
        <textarea
          {...inputProps}
          ref={sharedRef}
          rows={minRows}
          onChange={handleInput}
          onInput={() => adjustHeight()}
          style={{
            resize: 'none',
            minHeight: `${lineHeightRef.current * minRows}px`,
            maxHeight: `${lineHeightRef.current * maxRows}px`,
            overflow: 'auto',
          }}
          className={classNames(
            'w-full flex-1 p-3 text-lg',
            'leading-6',
            'resize-none',
            {
              'bg-app-input/90': !transparent,
              'bg-transparent': transparent,
            },
            'text-menu-text',
            'placeholder-opacity-30',
            'shadow focus:shadow-xl active:shadow-xl',
            'focus:outline-none focus:ring-0',
            'focus:placeholder-menu-active-secondary focus:bg-app-input/75',
            'active:text-menu-active active:bg-app-input active:border-opacity-100',
            {
              'border-animation animate-rotate-border-90': animate,
              'read-only:border-menu-active/20 read-only:hover:border-menu-active/10 read-only:text-menu-text/50 read-only:hover:text-menu-text/50 read-only:cursor-not-allowed read-only:shadow-none':
                isReadOnly,
              'disabled:border-menu/10 disabled:hover:border-menu/10 disabled:text-menu-text/75 text-menu-text/50 bg-app-input/60 disabled:cursor-not-allowed':
                isDisabled,
              'border-menu-active/50 placeholder-menu-text/30 hover:border-menu-active hover:text-menu-text hover:placeholder-menu-active-secondary focus:border-menu-active focus:text-menu-text hover:border-opacity-100 focus:border-opacity-100':
                !isDisabled && !isReadOnly,
            },
            className,
          )}
        />
        {props.children}
      </label>
    );
  },
);

export default InputTextArea;
